import { useState } from 'react';
import { Link, NavLink, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALERT_STATUS_LABELS,
  RULE_CATEGORIES,
  RULE_CATEGORY_LABELS,
  SEVERITY_LABELS,
  canHandleAlarm,
  canOpenContract,
  formatDateTime,
  formatRelative,
  type AlertStatus,
  type Paginated,
  type Permission,
  type Severity,
} from '@codepass/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import { Alert, Button, Card, Input, Select, Skeleton } from '@/components/ui';
import {
  AlertStatusBadge,
  EmptyState,
  FilterField,
  Money,
  PageHeader,
  Pagination,
  SEVERITY_BORDER,
  SeverityBadge,
} from '@/components/domain';
import {
  AlertActionButtons,
  AlertActionDialog,
  useAlertActions,
  type AlertAction,
} from '@/components/AlertActions';
import { cn } from '@/lib/cn';
import type { AlertFilterOptions, AlertItem } from '@/types';

const LIMIT = 25;

/**
 * Alarm-oversigt og -arkiv. PRD par. 8.1 og par. 8.3.
 *
 * Samme komponent til begge, fordi de er den samme liste set i to tilstande. Forskellen er
 * hvilke statusser der vises og hvad der giver mening at gøre: i arkivet er alarmerne lukkede,
 * saa der er ingen handlinger - kun loesningsnoten og soegningen.
 */

interface AlertSummary {
  critical: number;
  warning: number;
  info: number;
  snoozed: number;
  detection_last_run_at: string | null;
}

/** Kritisk foerst. PRD par. 8.1 naevner de tre tal i den raekkefoelge, og det er ikke tilfaeldigt. */
const SEVERITY_ORDER: readonly Severity[] = ['critical', 'warning', 'info'];

const OPEN_STATUSES: readonly AlertStatus[] = ['open', 'in_progress', 'snoozed'];
const ARCHIVED_STATUSES: readonly AlertStatus[] = ['resolved', 'dismissed'];

/**
 * Modtageren i adresselinjen (`recipient`): tom er dine egne, `all` er alles, ellers en brugers id.
 *
 * To indgange i skallen: **klokken er dine egne**, og **menupunktet er alles**. Klokken og
 * 'Nulstil filtre' lander paa /alarmer uden parametre, og derfor er tom - og ikke fx `me` -
 * standarden: de viser dine egne uden at skulle vide noget om filteret. Menupunktet og de links,
 * der viser et tal for alles alarmer - dashboardet, kontraktlisten - sender `recipient=all` med,
 * saa siden ikke viser faerre, end linket lovede.
 */
const ALL_RECIPIENTS = 'all';

/**
 * Den tomme liste siger, hvorfor den er tom. Paa standarden er det det vigtige: der kan ligge
 * alarmer hos andre, og uden hintet ligner "ingen alarmer" at alt er roligt.
 */
function emptyState(
  archived: boolean,
  narrowed: boolean,
  recipient: string,
  seesAll: boolean,
): { title: string; description?: string } {
  if (narrowed || (recipient && recipient !== ALL_RECIPIENTS)) {
    return { title: 'Ingen alarmer matcher filtrene', description: 'Prøv at fjerne et filter.' };
  }

  if (!recipient) {
    return {
      title: archived ? 'Du har ingen alarmer i arkivet' : 'Du har ingen åbne alarmer',
      // Hintet kun til den, der kan foelge det - Saelger og Udvikler ser kun deres egne.
      description: seesAll
        ? 'Vælg "Alle modtagere" under Modtager for at se alarmer hos de andre.'
        : undefined,
    };
  }

  return archived
    ? { title: 'Arkivet er tomt' }
    : {
        title: 'Ingen åbne alarmer',
        description: 'Detekteringsmotoren har ikke fundet noget der kræver handling.',
      };
}

/**
 * Fanerne tager modtageren med - men kun den. Kom man ind ad menupunktet til alles alarmer,
 * skal arkivet ogsaa vaere alles; de oevrige filtre hoerer til den enkelte liste, som foer.
 */
function SubNav() {
  const [params] = useSearchParams();
  const { can } = useAuth();
  const recipient = params.get('recipient');
  const query = recipient ? `?${new URLSearchParams({ recipient })}` : '';

  /* Arkivet er 'Se mine tidligere alarmer' - uden den er der kun de aabne. */
  const pages = [
    { to: '/alarmer', label: 'Åbne alarmer' },
    ...(can('alerts.archive_own') ? [{ to: '/alarmer/arkiv', label: 'Arkiv' }] : []),
  ];
  return (
    <nav className="mb-4 flex gap-1 border-b border-line">
      {pages.map((page) => (
        <NavLink
          key={page.to}
          to={`${page.to}${query}`}
          end
          className={({ isActive }) =>
            cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              isActive
                ? 'border-brand font-medium text-brand-800'
                : 'border-transparent text-ink-muted hover:text-ink',
            )
          }
        >
          {page.label}
        </NavLink>
      ))}
    </nav>
  );
}

function AlertCard({
  alert,
  archived,
  showRecipient,
  busy,
  onAct,
}: {
  alert: AlertItem;
  archived: boolean;
  /** Kun naar listen ikke er dine egne - ellers ville dit navn staa paa hvert eneste kort. */
  showRecipient: boolean;
  busy: boolean;
  onAct: (action: AlertAction) => void;
}) {
  /* En udvikler kan faa en alarm om en kontrakt, rollen ikke kan aabne - saa er navnet tekst. */
  const { can, user } = useAuth();
  const actionLink = suggestedActionLink(alert);
  /* 'Se alle alarmer' reads someone else's alarm; acting on it is 'Behandl alle alarmer'. */
  const handles = canHandleAlarm(user, alert.recipient_id);

  return (
    <li className={cn('card border-l-4 p-4', SEVERITY_BORDER[alert.severity])}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/alarmer/${alert.id}`} className="text-sm font-semibold hover:underline">
              {alert.title}
            </Link>
            <SeverityBadge severity={alert.severity} />
            <AlertStatusBadge status={alert.status} />
          </div>

          <p className="mt-1 text-xs text-ink-muted">{alert.description}</p>

          <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
            <span>{RULE_CATEGORY_LABELS[alert.rule.category]}</span>
            {showRecipient && alert.recipient ? (
              <>
                <span>·</span>
                <span>Modtager: {alert.recipient.name}</span>
              </>
            ) : null}
            {alert.contract ? (
              <>
                <span>·</span>
                {canOpenContract(user, alert.contract) ? (
                  <Link to={`/kontrakter/${alert.contract.id}`} className="hover:underline">
                    {alert.contract.title}
                  </Link>
                ) : (
                  <span>{alert.contract.title}</span>
                )}
              </>
            ) : null}
            {alert.customer ? (
              <>
                <span>·</span>
                {can('customers.view') ? (
                  <Link to={`/kunder/${alert.customer.id}`} className="hover:underline">
                    {alert.customer.name}
                  </Link>
                ) : (
                  <span>{alert.customer.name}</span>
                )}
              </>
            ) : null}
            <span>·</span>
            <span>{formatRelative(alert.created_at)}</span>
            {alert.snoozed_until ? (
              <>
                <span>·</span>
                <span>Tilbage {formatDateTime(alert.snoozed_until)}</span>
              </>
            ) : null}
          </p>

          {/* I arkivet er noten det vigtigste paa kortet: den er hele grunden til at gemme det. */}
          {archived && alert.resolution_note ? (
            <p className="mt-2 rounded-md bg-canvas p-2 text-xs">{alert.resolution_note}</p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          {alert.suggested_amount_excl_vat !== null ? (
            <Money
              value={alert.suggested_amount_excl_vat}
              currency={alert.contract?.currency ?? 'DKK'}
              className="text-sm font-semibold"
            />
          ) : null}

          <div className="flex flex-wrap justify-end gap-2">
            {alert.suggested_action && canOpen(actionLink, can) ? (
              <Link to={actionLink}>
                <Button size="sm">{shortAction(alert.suggested_action)}</Button>
              </Link>
            ) : null}
            {handles ? (
              <AlertActionButtons status={alert.status} busy={busy} onAct={onAct} />
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}

/**
 * Foreslaaet handling foerer til det sted handlingen udfoeres, med kontrakten forudfyldt.
 *
 * Navnene er regelraekkernes egne i public.codepass_domain_rules - de danske. Rammer ingen af
 * dem, ender knappen paa kontrakten, hvor der altid er noget at gaa videre med.
 */
function suggestedActionLink(alert: AlertItem): string {
  const contractId = alert.contract?.id;
  switch (alert.rule.name) {
    case 'manglende_faktura_milepael':
    case 'manglende_faktura_recurring':
    case 'ufakturerede_timer_aldrende':
    case 'klar_til_slutfaktura':
      return `/fakturering/ny?contract_id=${contractId ?? ''}`;
    case 'ikke_matchede_timer':
      return '/timer?unmatched_only=true';
    case 'fakturaudkast_godkendt_ikke_sendt':
      return '/fakturering?status=approved';
    default:
      return contractId ? `/kontrakter/${contractId}` : `/alarmer/${alert.id}`;
  }
}

/**
 * Om rollen kan aabne den side, en foreslaaet handling peger paa. En knap, der ender paa
 * /forbudt, er vaerre end ingen knap.
 */
function canOpen(link: string, can: (permission: Permission) => boolean): boolean {
  if (link.startsWith('/fakturering/ny')) return can('invoices.create');
  if (link.startsWith('/fakturering')) return can('invoices.drafts.view');
  if (link.startsWith('/kontrakter')) return can('contracts.view');
  if (link.startsWith('/timer')) return can('hours.view');
  return true;
}

function shortAction(action: string): string {
  return action.length > 34 ? `${action.slice(0, 32)}…` : action;
}

function AlertList({ archived }: { archived: boolean }) {
  const [params, setParams] = useSearchParams();
  const [offset, setOffset] = useState(0);
  const { can, user } = useAuth();
  const queryClient = useQueryClient();
  const actions = useAlertActions();
  /*
   * Uden 'Se alle alarmer' - eller 'Se alle tidligere alarmer' i arkivet - ser man kun sine egne.
   * API'et afgraenser, og vaelgeren er vaek.
   */
  const seesAll = can(archived ? 'alerts.archive_all' : 'alerts.view_all');

  const severity = params.get('severity') ?? '';
  const status = params.get('status') ?? '';
  const category = params.get('category') ?? '';
  const contractId = params.get('contract_id') ?? '';
  const customerId = params.get('customer_id') ?? '';
  const search = params.get('search') ?? '';

  /* Dit eget id i adresselinjen er standarden skrevet ud - saa staar valget ogsaa paa 'Mine'. */
  const rawRecipient = params.get('recipient') ?? '';
  const recipient = rawRecipient === user?.id ? '' : rawRecipient;
  /** Modtageren API'et afgraenses til. Undefined er alles. */
  const recipientId = recipient === ALL_RECIPIENTS ? undefined : recipient || user?.id;

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
    setOffset(0);
  };

  const clearFilters = (): void => {
    setParams(new URLSearchParams(), { replace: true });
    setOffset(0);
  };

  const narrowed = Boolean(severity || status || category || contractId || customerId || search);
  /* Modtageren taeller kun med, naar den er flyttet fra standarden. */
  const filtered = narrowed || Boolean(recipient);

  /* Samme noegle som klokken i AppShell, saa de to deler svaret, naar listen staar paa dine egne. */
  const { data: summary } = useQuery({
    queryKey: ['alerts', 'summary', recipientId ?? ALL_RECIPIENTS],
    queryFn: () => api.get<AlertSummary>('/alerts/summary', { recipient_id: recipientId }),
  });

  const { data: options } = useQuery({
    queryKey: ['alerts', 'filters'],
    queryFn: () => api.get<AlertFilterOptions>('/alerts/filters'),
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'alerts',
      { severity, status, category, contractId, customerId, search, recipientId, archived, offset },
    ],
    queryFn: () =>
      api.get<Paginated<AlertItem>>('/alerts', {
        severity: severity || undefined,
        status: status || undefined,
        category: category || undefined,
        contract_id: contractId || undefined,
        customer_id: customerId || undefined,
        search: search || undefined,
        recipient_id: recipientId,
        archived: archived ? 'true' : undefined,
        limit: LIMIT,
        offset,
      }),
  });

  const runDetection = useMutation({
    mutationFn: () => api.post('/alerts/run-detection'),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['alerts'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]);
    },
  });

  const statuses = archived ? ARCHIVED_STATUSES : OPEN_STATUSES;

  return (
    <>
      <PageHeader
        title={archived ? 'Alarm-arkiv' : 'Alarmer'}
        description={
          archived
            ? 'Løste og afviste alarmer med deres begrundelse. Søgbar for retrospektiv analyse. ' +
              'Alarmer slettes kun af reglen "Oprydning i alarm-historikken", efter fristen for ' +
              'deres status.'
            : summary?.detection_last_run_at
              ? `Detektering kørt ${formatRelative(summary.detection_last_run_at)}`
              : 'Detekteringsmotoren har ikke kørt endnu'
        }
        actions={
          /* "Kør nu" sætter reglerne - og deres Teams-beskeder - i gang: 'Ændr regler'. */
          can('rules.manage') && !archived ? (
            <Button
              size="sm"
              variant="secondary"
              loading={runDetection.isPending}
              onClick={() => runDetection.mutate()}
            >
              Kør nu
            </Button>
          ) : undefined
        }
      />
      <SubNav />

      {/* Statusbjaelken. Tallene er klikbare filtre, som PRD par. 8.1 foreskriver. */}
      {!archived ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {SEVERITY_ORDER.map((option) => {
            const count = summary ? summary[option] : 0;
            const active = severity === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => setParam('severity', active ? '' : option)}
                className={cn(
                  'min-w-28 rounded-md border px-3 py-2 text-left text-sm',
                  active ? 'border-brand bg-brand-50' : 'border-line bg-surface hover:bg-canvas',
                )}
              >
                <span className="block text-xs text-ink-muted">{SEVERITY_LABELS[option]}</span>
                <span className="block text-lg font-semibold tabular">{count}</span>
              </button>
            );
          })}

          {summary && summary.snoozed > 0 ? (
            <button
              type="button"
              onClick={() => setParam('status', status === 'snoozed' ? '' : 'snoozed')}
              className="text-xs text-ink-muted underline-offset-2 hover:underline"
            >
              {summary.snoozed} udsat
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        {/* Foerst i raekken: det er det filter, der afgoer hvis liste det overhovedet er. */}
        {seesAll ? (
          <div className="w-full sm:w-48">
            <FilterField label="Modtager">
              <Select
                value={recipient}
                onChange={(event) => setParam('recipient', event.target.value)}
              >
                <option value="">Mine alarmer</option>
                <option value={ALL_RECIPIENTS}>Alle modtagere</option>
                {(options?.recipients ?? [])
                  .filter((option) => option.id !== user?.id)
                  .map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
              </Select>
            </FilterField>
          </div>
        ) : null}

        <div className="w-full sm:w-64">
          <FilterField label="Søg">
            <Input
              placeholder="Titel, beskrivelse eller note"
              value={search}
              onChange={(event) => setParam('search', event.target.value)}
            />
          </FilterField>
        </div>

        <div className="w-full sm:w-44">
          <FilterField label="Status">
            <Select value={status} onChange={(event) => setParam('status', event.target.value)}>
              <option value="">Alle statusser</option>
              {statuses.map((option) => (
                <option key={option} value={option}>
                  {ALERT_STATUS_LABELS[option]}
                </option>
              ))}
            </Select>
          </FilterField>
        </div>

        <div className="w-full sm:w-48">
          <FilterField label="Kategori">
            <Select value={category} onChange={(event) => setParam('category', event.target.value)}>
              <option value="">Alle kategorier</option>
              {RULE_CATEGORIES.map((option) => (
                <option key={option} value={option}>
                  {RULE_CATEGORY_LABELS[option]}
                </option>
              ))}
            </Select>
          </FilterField>
        </div>

        <div className="w-full sm:w-52">
          <FilterField label="Kunde">
            <Select
              value={customerId}
              onChange={(event) => setParam('customer_id', event.target.value)}
            >
              <option value="">Alle kunder</option>
              {(options?.customers ?? []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FilterField>
        </div>

        <div className="w-full sm:w-52">
          <FilterField label="Kontrakt">
            <Select
              value={contractId}
              onChange={(event) => setParam('contract_id', event.target.value)}
            >
              <option value="">Alle kontrakter</option>
              {(options?.contracts ?? []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FilterField>
        </div>

        {filtered ? (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            Nulstil filtre
          </Button>
        ) : null}
      </div>

      {actions.error ? (
        <Alert tone="error" className="mb-3">
          {actions.error}
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-28" />
          ))}
        </div>
      ) : error ? (
        <Alert tone="error">Kunne ikke hente alarmer.</Alert>
      ) : (data?.data.length ?? 0) === 0 ? (
        <Card>
          <EmptyState {...emptyState(archived, narrowed, recipient, seesAll)} />
        </Card>
      ) : (
        <>
          <ul className="space-y-3">
            {data?.data.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                archived={archived}
                showRecipient={Boolean(recipient)}
                busy={actions.busy}
                onAct={(action) => actions.run(alert.id, action)}
              />
            ))}
          </ul>
          <div className="card mt-3">
            <Pagination
              total={data?.total ?? 0}
              limit={LIMIT}
              offset={offset}
              onChange={setOffset}
            />
          </div>
        </>
      )}

      <AlertActionDialog {...actions.dialogProps} />
    </>
  );
}

export function AlertsPage() {
  return <AlertList archived={false} />;
}

export function AlertArchivePage() {
  return <AlertList archived />;
}

export type { Severity };
